import React, { useRef, useState, useEffect, FC } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { iconSize } from '../../../../constants/icon.constants';
import useMediaQuery from '../../../../hooks/useMediaQuery';
import { windowService } from '../../../../utils/window.utils';
import Icon from '../../../@shared/icon';
import { TCompatibleLoadersTabsProps } from '../../types/contentHubCompatibleLoaders.types';

const CompatibleLoadersTabs: FC<WithTranslation & TCompatibleLoadersTabsProps> = ({ tabs, selectedTab, onSelect }) => {
  const tabContainerEl = useRef<HTMLDivElement | null>(null);
  const [hideArrows, setHideArrows] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    function resizeHandler(): void {
      onSelect(0);
    }

    resizeHandler();

    windowService().addEventListener('resize', resizeHandler);
    return (): void => windowService().removeEventListener('resize', resizeHandler);
  }, [tabContainerEl, windowService]); // eslint-disable-line

  useEffect(() => {
    if (tabContainerEl?.current) {
      const tabContainerDim = tabContainerEl.current.getBoundingClientRect();
      const tabChildrens = tabContainerEl.current.children;
      if (tabChildrens?.length) {
        const lastChild = tabChildrens[tabChildrens.length - 1];
        if (lastChild) {
          const lastChildDim = lastChild.getBoundingClientRect();
          if (tabContainerDim.left > 0 && lastChildDim.right - tabContainerDim.right < 0) {
            tabContainerEl.current.style.justifyContent = 'flex-start';
            setHideArrows(true);
          }
        }
      }
    }
  }, [tabContainerEl, selectedTab, tabs]);

  const scrollTab = (direction: string): void => {
    if (tabContainerEl?.current) {
      const tabChildren = tabContainerEl.current.children[0];
      const tabDimension = tabChildren.getBoundingClientRect().width;

      if (direction === 'right') {
        tabContainerEl.current.style.right = `${+tabContainerEl.current.style.right.split('p')[0] + tabDimension}px`;
        setShowLeftArrow(true);
        if (+tabContainerEl.current.style.right.split('p')[0] > tabContainerEl.current.scrollWidth - tabContainerEl.current.offsetWidth) {
          tabContainerEl.current.style.right = `${tabContainerEl.current.scrollWidth - tabContainerEl.current.offsetWidth}px`;
          setShowRightArrow(false);
        }
      } else {
        tabContainerEl.current.style.right = `${+tabContainerEl.current.style.right.split('p')[0] - tabDimension}px`;
        setShowRightArrow(true);
        if (+tabContainerEl.current.style.right.split('p')[0] < 0) {
          tabContainerEl.current.style.right = '0px';
          setShowLeftArrow(false);
        }
      }
    }
  };

  useEffect(() => {
    if (isMobile || hideArrows) return;
  }, [tabContainerEl, selectedTab, tabs, isMobile]); // eslint-disable-line

  const goToPreviousSlide = (): void => {
    scrollTab('left');
  };

  const goToNextSlide = (): void => {
    scrollTab('right');
  };

  return (
    <div className={'ch-compatible-loaders__tabs-container'}>
      <section className={'ch-compatible-loaders__tabs'}>
        <div className="ch-compatible-loaders__slider-wrapper" ref={tabContainerEl}>
          {tabs?.map((tab, index: number) => (
            <h3
              key={tab?.id}
              onClick={() => onSelect(index)}
              className={`ch-compatible-loaders__tab ${index === selectedTab ? 'ch-compatible-loaders__tab--selected' : ''}`}
            >
              {tab.title}
            </h3>
          ))}
        </div>
        <button
          className={`
            ch-compatible-loaders__arrow
            ch-compatible-loaders__arrow--left
            ${isMobile || hideArrows || !showLeftArrow ? 'ch-compatible-loaders__arrow--hide' : ''}
          `}
          onClick={goToPreviousSlide}
        >
          <Icon iconName="arrow-chevron-left" color="white" size={iconSize['md']} extraClassName="ch-compatible-loaders__arrow-icon" />
        </button>
        <button
          className={`
            ch-compatible-loaders__arrow
            ch-compatible-loaders__arrow--right
            ${isMobile || hideArrows || !showRightArrow ? 'ch-compatible-loaders__arrow--hide' : ''}
          `}
          onClick={goToNextSlide}
        >
          <Icon iconName="arrow-chevron-right" color="white" size={iconSize['md']} extraClassName="ch-compatible-loaders__arrow-icon" />
        </button>
      </section>
    </div>
  );
};

export default withTranslation()(CompatibleLoadersTabs);
